/**
 * Hook for fetching user profile with usage stats.
 *
 * Uses TanStack Query for data fetching with caching.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export interface UserProfile {
  id: number;
  email: string;
  name?: string;
  picture_url?: string;
  tier: string;
  is_admin: boolean;
  is_pro: boolean;
  total_input_tokens: number;
  total_output_tokens: number;
  total_thinking_tokens: number;
  total_requests: number;
  estimated_cost_usd: number;
  spending_limit_usd: number | null;
  spending_remaining_usd: number | null;
  is_over_limit: boolean;
  calendar_enabled: boolean;
  has_sheet: boolean;
}

export function useUserProfile() {
  const { isAuthenticated } = useAuth();

  return useQuery<UserProfile>({
    queryKey: ['user', 'profile'],
    queryFn: async () => {
      const response = await apiClient.userProfile.get();
      return response.data;
    },
    enabled: isAuthenticated,
    staleTime: 30 * 1000, // 30 seconds - refresh fairly often for usage stats
    gcTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: true, // Refresh when user comes back to tab
  });
}
